import { useId, useMemo } from 'react';

interface SparklineCardProps {
  title: string;
  subtitle: string;
  values: number[];
  tone?: 'light' | 'night';
}

const SparklineCard = ({ title, subtitle, values, tone = 'light' }: SparklineCardProps) => {
  const chartId = useId();
  const points = useMemo(() => {
    if (values.length === 0) {
      return '';
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(1, max - min);

    return values
      .map((value, index) => {
        const x = (index / Math.max(1, values.length - 1)) * 100;
        const normalized = ((value - min) / range) * 30;
        const y = 38 - normalized;
        return `${x},${y}`;
      })
      .join(' ');
  }, [values]);
  const bars = useMemo(() => {
    if (values.length === 0) {
      return [];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(1, max - min);

    return values.map((value, index) => {
      const x = (index / Math.max(1, values.length - 1)) * 100;
      const normalized = ((value - min) / range) * 100;
      const height = 8 + normalized * 0.3;
      return { x, height };
    });
  }, [values]);

  const classNameByTone =
    tone === 'night'
      ? {
          shell: 'border-slate-700/70 bg-[#111b2f]',
          title: 'text-slate-100',
          subtitle: 'text-slate-400',
          stroke: '#f59e0b',
          lineStroke: '#7dd3fc',
          fillStart: '#fb923c',
          fillEnd: '#fb923c',
          grid: 'stroke-slate-700',
          legendPrimary: 'text-orange-300',
          legendSecondary: 'text-sky-300',
        }
      : {
          shell: 'border-slate-200/80 bg-white',
          title: 'text-slate-900',
          subtitle: 'text-slate-500',
          stroke: '#f97316',
          lineStroke: '#0ea5e9',
          fillStart: '#fb923c',
          fillEnd: '#fdba74',
          grid: 'stroke-slate-200',
          legendPrimary: 'text-orange-600',
          legendSecondary: 'text-sky-600',
        };

  return (
    <article className={`rounded-2xl border p-4 shadow-[0_14px_30px_-24px_rgba(15,23,42,0.48)] ${classNameByTone.shell}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className={`text-sm font-semibold sm:text-base ${classNameByTone.title}`}>{title}</h3>
          <p className={`mt-1 text-xs ${classNameByTone.subtitle}`}>{subtitle}</p>
        </div>
        <span className={`rounded-lg px-2 py-1 text-[10px] font-semibold sm:text-[11px] ${tone === 'night' ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
          8 WKS
        </span>
      </div>

      <svg viewBox="0 0 100 42" className="mt-4 h-24 w-full">
        <defs>
          <linearGradient id={`${chartId}-sparkline`} x1="0%" x2="0%" y1="0%" y2="100%">
            <stop offset="0%" stopColor={classNameByTone.fillStart} stopOpacity="0.45" />
            <stop offset="100%" stopColor={classNameByTone.fillEnd} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        {[8, 16, 24, 32, 40].map((gridY) => (
          <line key={gridY} x1="0" y1={gridY} x2="100" y2={gridY} className={classNameByTone.grid} strokeWidth="0.45" />
        ))}
        {bars.map((bar, index) => (
          <rect
            key={`${bar.x}-${index}`}
            x={Math.max(0, bar.x - 1.4)}
            y={40 - bar.height}
            width="2.8"
            height={bar.height}
            rx="0.8"
            fill={classNameByTone.stroke}
            opacity={0.82}
          />
        ))}
        <polyline
          points={`0,42 ${points} 100,42`}
          fill={`url(#${chartId}-sparkline)`}
          stroke="none"
        />
        <polyline points={points} fill="none" stroke={classNameByTone.lineStroke} strokeWidth="1.4" strokeLinecap="round" />
      </svg>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] font-semibold">
        <span className={classNameByTone.legendPrimary}>Page Views</span>
        <span className={classNameByTone.legendSecondary}>Clicks</span>
      </div>
    </article>
  );
};

export default SparklineCard;
